import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Card, CardBody, Row, Col, Input, Button, Spinner, Alert, Badge,
} from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import FeatherIcon from 'feather-icons-react';
import PermissionGuard from '../components/Common/PermissionGuard';
import { TEAM_CREATE } from '../constants/permissions';
import {
  fetchTeams, clearTeamMessages,
} from '../store/team/actions';
import TeamModal from '../components/TeamModal';
import TeamDetailModal from '../components/TeamDetailModal';

const Teams = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    teams, teamsLoading, teamsError,
    saveSuccess, saveError,
  } = useSelector((s) => s.Team);

  const [search, setSearch] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [editTeam, setEditTeam] = useState(null);
  const [detailTeamId, setDetailTeamId] = useState(null);
  const [detailInitialTab, setDetailInitialTab] = useState('members');
  const [detailAutoOpenAddMembers, setDetailAutoOpenAddMembers] = useState(false);

  useEffect(() => {
    dispatch(fetchTeams());
  }, [dispatch]);

  useEffect(() => {
    if (saveSuccess || saveError) {
      const timer = setTimeout(() => dispatch(clearTeamMessages()), 4000);
      return () => clearTimeout(timer);
    }
  }, [saveSuccess, saveError, dispatch]);

  const filteredSorted = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = (teams || []).filter((tm) => {
      if (!term) return true;
      return (tm.name || '').toLowerCase().includes(term)
        || (tm.description || '').toLowerCase().includes(term);
    });
    return list.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [teams, search]);

  const handleCreate = () => { setEditTeam(null); setCreateOpen(true); };
  const handleEditFromDetail = (team) => { setEditTeam(team); setCreateOpen(true); };
  const handleSaved = useCallback((created) => {
    const wasCreate = !editTeam && created && created.id;
    setCreateOpen(false);
    setEditTeam(null);
    dispatch(fetchTeams());
    if (wasCreate) {
      // Land the user on the Members tab with the add-member picker already
      // open — adding members is the most common next step after creating a team.
      setDetailInitialTab('members');
      setDetailAutoOpenAddMembers(true);
      setDetailTeamId(created.id);
    }
  }, [dispatch, editTeam]);
  const handleDeleted = useCallback(() => {
    setDetailTeamId(null);
    dispatch(fetchTeams());
  }, [dispatch]);
  const handleDetailClose = useCallback(() => {
    setDetailTeamId(null);
    setDetailInitialTab('members');
    setDetailAutoOpenAddMembers(false);
  }, []);

  const initials = (name) => (name || '').trim().split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || '?';

  return (
    <div className="page-content">
      <div className="container-fluid">
        {saveSuccess && <Alert color="success" toggle={() => dispatch(clearTeamMessages())}>{saveSuccess}</Alert>}
        {saveError && <Alert color="danger" toggle={() => dispatch(clearTeamMessages())}>{saveError}</Alert>}

        <div className="mb-3 d-flex flex-wrap align-items-center gap-2">
          <PermissionGuard permission={TEAM_CREATE}>
            <Button color="success" className="d-flex align-items-center gap-2" onClick={handleCreate}>
              <FeatherIcon icon="plus" size={16} />
              {t('teams.create', 'Create Team')}
            </Button>
          </PermissionGuard>
          <div style={{ minWidth: 280, flexGrow: 1, maxWidth: 480 }}>
            <Input
              type="text"
              placeholder={t('teams.search.placeholder', 'Search teams...')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {teamsLoading && (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        )}

        {!teamsLoading && teamsError && (
          <Alert color="danger">
            <div>{t('teams.error.loadFailed', 'Failed to load teams.')}</div>
            <small>{teamsError}</small>
          </Alert>
        )}

        {!teamsLoading && !teamsError && filteredSorted.length === 0 && (
          <Card>
            <CardBody className="text-center text-muted py-5">
              <FeatherIcon icon="users" size={36} className="mb-2 text-muted" />
              <div>{search.trim() ? t('teams.noResults', 'No teams match your search.') : t('teams.empty', 'No teams yet')}</div>
            </CardBody>
          </Card>
        )}

        {!teamsLoading && !teamsError && filteredSorted.length > 0 && (
          <Row className="g-3">
            <AnimatePresence>
              {filteredSorted.map((team) => (
                <Col xs={12} sm={6} lg={4} key={team.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Card
                      className="team-card"
                      role="button"
                      tabIndex={0}
                      onClick={() => setDetailTeamId(team.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter') setDetailTeamId(team.id); }}
                      style={{ cursor: 'pointer', borderLeft: `4px solid ${team.color || '#0E7C7B'}` }}
                    >
                      <CardBody>
                        <div className="d-flex align-items-center gap-3">
                          <div
                            style={{
                              width: 60, height: 60, borderRadius: '50%',
                              background: team.photoUrl ? `url(${team.photoUrl}) center/cover no-repeat` : (team.color || '#0E7C7B'),
                              color: '#fff',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: 22, fontWeight: 600, flexShrink: 0,
                            }}
                          >
                            {!team.photoUrl && initials(team.name)}
                          </div>
                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <div className="d-flex align-items-center justify-content-between">
                              <h6 className="mb-0 text-truncate" title={team.name}>{team.name}</h6>
                              <div className="d-flex align-items-center ms-2 flex-shrink-0 gap-1">
                                {team.restrictVisibility && (
                                  <Badge
                                    color="light"
                                    className="text-dark"
                                    title={t('teams.restrictVisibility.help', "Members only see assets whose type is assigned to this team, and the work orders linked to them.")}
                                  >
                                    <FeatherIcon icon="lock" size={11} className="me-1" />
                                    {t('teams.restrictVisibility.badge', 'Restricted')}
                                  </Badge>
                                )}
                                <Badge color="light" className="text-dark">
                                  <FeatherIcon icon="users" size={11} className="me-1" />
                                  {team.memberCount ?? 0}
                                </Badge>
                              </div>
                            </div>
                            {team.description && (
                              <div className="text-muted small text-truncate mt-1" title={team.description}>
                                {team.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </motion.div>
                </Col>
              ))}
            </AnimatePresence>
          </Row>
        )}
      </div>

      <TeamModal
        isOpen={createOpen}
        toggle={() => { setCreateOpen(false); setEditTeam(null); }}
        team={editTeam}
        onSaved={handleSaved}
      />

      <TeamDetailModal
        isOpen={!!detailTeamId}
        teamId={detailTeamId}
        toggle={handleDetailClose}
        onEdit={handleEditFromDetail}
        onDeleted={handleDeleted}
        initialTab={detailInitialTab}
        autoOpenAddMembers={detailAutoOpenAddMembers}
      />

      <style>{`
        .team-card {
          transition: box-shadow 0.15s ease, transform 0.15s ease;
        }
        .team-card:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          transform: translateY(-1px);
        }
      `}</style>
    </div>
  );
};

export default Teams;
